import { forwardRef, type HTMLAttributes } from 'react';
import { cx } from '@linaria/atomic';
import { NakedButton } from '../../naked';
import { Avatar, type AvatarProps } from '../avatar/avatar.component';
import { type SizeProps } from '../avatar/avatar.styles';
import {
  groupContainerStyles,
  itemAvatarStyles,
  itemWrapperStyles,
  overflowAvatarStyles,
} from './circular-avatar-group.styles';

const MAX_VISIBLE_AVATARS_LENGTH = 4;

const getChildSize = (parentSize: SizeProps, count: number): SizeProps => {
  const sizes: SizeProps[] = ['xxs', 'xs', 'small', 'medium', 'large', 'xl', 'xxl'];
  const currentIndex = sizes.indexOf(parentSize);

  // Scale down 1 step for 2 recipients, 2 steps for 3-4 recipients
  const stepDown = count === 2 ? 1 : 2;
  const childIndex = Math.max(0, currentIndex - stepDown);

  return sizes[childIndex] as SizeProps;
};

const getRecipientLabel = ({ firstName, lastName, name, title }: AvatarProps) => {
  if (firstName && lastName) return `${firstName} ${lastName}`;
  return name || title || 'Unnamed recipient';
};

export interface CircularAvatarGroupProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  recipients: AvatarProps[];
  size?: SizeProps;
  onOverflowClick?: (e: React.MouseEvent<HTMLElement>) => void;
  disableRecipientHoverLabel?: boolean;
}

export const CircularAvatarGroup = forwardRef<HTMLDivElement, CircularAvatarGroupProps>(
  ({ recipients, size = 'medium', onOverflowClick, disableRecipientHoverLabel, className, ...rest }, ref) => {
    const count = recipients.length;
    const firstRecipient = recipients[0];
    const recipientNames = recipients.map(getRecipientLabel);
    const recipientSummary = recipientNames.slice(0, 4).join(', ');
    const childSize = getChildSize(size, count);

    const groupAriaLabel =
      count <= 0
        ? 'No recipients'
        : count === 1
        ? `${recipientNames[0]} recipient`
        : count > MAX_VISIBLE_AVATARS_LENGTH
        ? `${count} recipients: ${recipientSummary}, and more`
        : `${count} recipients: ${recipientSummary}`;

    const overflowLabel = `View ${count} recipients`;

    return (
      <div
        ref={ref}
        className={cx(groupContainerStyles(size), className)}
        role='group'
        aria-label={groupAriaLabel}
        {...rest}
      >
        {count === 0 && <Avatar size={size} disableHoverLabel={disableRecipientHoverLabel} />}

        {count === 1 && firstRecipient && (
          <Avatar
            {...firstRecipient}
            size={size}
            disableHoverLabel={disableRecipientHoverLabel ?? firstRecipient.disableHoverLabel}
          />
        )}

        {count > 1 &&
          count <= MAX_VISIBLE_AVATARS_LENGTH &&
          recipients.map((recipient, index) => {
            const isInteractive = !!recipient.onClick && !recipient.disableClick;
            const wrapperStyles = itemWrapperStyles(count, index);

            return (
              <div key={recipient.id || recipient.userId} className={wrapperStyles}>
                <Avatar
                  {...recipient}
                  size={childSize}
                  className={itemAvatarStyles(childSize, isInteractive)}
                  disableHoverLabel={disableRecipientHoverLabel ?? recipient.disableHoverLabel}
                />
              </div>
            );
          })}

        {count > MAX_VISIBLE_AVATARS_LENGTH &&
          (onOverflowClick ? (
            <NakedButton
              className={overflowAvatarStyles(size, true)}
              onClick={onOverflowClick}
              aria-label={overflowLabel}
              data-testid='circular-avatar-group-overflow-button'
            >
              {count}
            </NakedButton>
          ) : (
            <div className={overflowAvatarStyles(size, false)} aria-hidden>
              {count}
            </div>
          ))}
      </div>
    );
  }
);

CircularAvatarGroup.displayName = 'CircularAvatarGroup';
